/*
 * @providesModule BannerModal
 */

import React, {useEffect, useRef} from 'react';
import {Animated, Dimensions, Platform, Text, TouchableOpacity, View} from 'react-native';
import styles, {BANNER_WIDTH} from 'BannerStyle';
import SkillzSVG from 'SkillzSVG';
import {_m} from 'INTL';

interface BannerProps {
  //close handler
  onClosePress: () => void;
  //resend email handler
  onResendPress?: () => Promise<void>;
  //Text for title
  title: string;
  //Text for message
  message: string;
  //number of lines and height so box is right
  numberOfLines: number;
  heightContainer: number;
  //if true shows error icon, false info icon
  error: boolean;
  //have resend email hyperlink text
  haveResendEmail?: boolean;
}

const TEST_IDS = {
  modalTestID: 'banner_modal',
  titleTestID: 'banner_modal_title',
  messageTestID: 'banner_modal_message',
  resendTestID: 'banner_modal_resend',
  closeTestID: 'banner_modal_close',
  sigInToContinueTestID: 'banner_modal_sign_in_to_continue',
};

const BannerModal = (props: BannerProps) => {
  const translateX = useRef(new Animated.Value(400)).current;

  useEffect(() => {
    // Reset animation to start position (off-screen to the right)
    translateX.setValue(400);

    // Animate banner sliding in from right to center
    Animated.timing(translateX, {
      toValue: 0, // Slide to centered position (alignSelf: center handles the centering)
      useNativeDriver: true,
      duration: 300,
    }).start();
  }, [props.title, props.message]);

  const closeAnimation = () => {
    Animated.timing(translateX, {
      toValue: -400, // Slide out to the left
      useNativeDriver: true,
      duration: 200,
    }).start(props.onClosePress);
  };

  const renderBarIcon = () => {
    if (props.error) {
      return <View style={styles.smallBarErrorIcon} />;
    } else if (props.numberOfLines > 2) {
      return <View style={styles.largeBarIcon} />;
    }
    return <View style={styles.smallBarInfoIcon} />;
  };

  const renderIcon = () => {
    if (props.error) {
      return <SkillzSVG source={'info_error.svg'} style={styles.icon} />;
    }
    return <SkillzSVG source={'info.svg'} style={styles.icon} />;
  };

  const renderTextContainer = () => {
    return (
      <View style={styles.textContainer}>
        <Text style={styles.titleText} testID={TEST_IDS.titleTestID}>
          {props.title}
        </Text>
        <Text
          style={styles.messageText}
          testID={TEST_IDS.messageTestID}
          numberOfLines={props.numberOfLines}
        >
          {props.message}
        </Text>
        {renderHyperLink()}
      </View>
    );
  };

  const renderCloseBtn = () => {
    return (
      <TouchableOpacity
        style={styles.closeBtn}
        testID={TEST_IDS.closeTestID}
        onPress={closeAnimation}
      >
        <SkillzSVG source={'close.svg'} style={styles.closeIcons} />
      </TouchableOpacity>
    );
  };

  const renderHyperLink = () => {
    if (props.haveResendEmail) {
      return (
        <TouchableOpacity onPress={props.onResendPress} testID={TEST_IDS.resendTestID}>
          <Text style={styles.hyperLink}>{_m('Resend email')}</Text>
        </TouchableOpacity>
      );
    }
    return;
  };

  return (
    <Animated.View
      style={[
        Platform.OS === 'ios' ? styles.containerIos : styles.containerAndroid,
        {height: props.heightContainer},
        {
          transform: [{translateX: translateX}],
          alignSelf: 'center', // Center the banner horizontally
          maxWidth:
            Platform.OS === 'android'
              ? Dimensions.get('window').width - 100
              : Dimensions.get('window').width - 32, // Prevent overflow with 16px margins
        },
      ]}
      testID={TEST_IDS.modalTestID}
    >
      {renderBarIcon()}
      {renderIcon()}
      {renderTextContainer()}
      {renderCloseBtn()}
    </Animated.View>
  );
};

export default BannerModal;
